import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

type BentoSpan = "standard" | "wide" | "tall" | "full";

interface BentoCardProps {
  title: string;
  description?: string;
  icon?: LucideIcon;
  span?: BentoSpan;
  glowColor?: string;
  href?: string;
  children: React.ReactNode;
  className?: string;
  index?: number;
}

const spanClasses: Record<BentoSpan, string> = {
  standard: "col-span-1",
  wide: "col-span-1 md:col-span-2",
  tall: "col-span-1 row-span-2",
  full: "col-span-1 md:col-span-4",
};

export function BentoCard({
  title,
  description,
  icon: Icon,
  span = "standard",
  glowColor,
  href,
  children,
  className,
  index = 0,
}: BentoCardProps) {
  const cardInner = (
    <>
      {/* Card body */}
      <div className="flex-1 min-h-0">{children}</div>
      {/* Bottom label strip */}
      <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.06)] flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#5A6A68] shrink-0" />}
        <span className="font-semibold text-[13px] text-foreground leading-tight">
          {title}
        </span>
        {description && (
          <span className="text-[12px] text-[#5A6A68] truncate">
            {description}
          </span>
        )}
      </div>
    </>
  );

  const motionProps = {
    initial: { opacity: 0, y: 12 } as const,
    animate: { opacity: 1, y: 0 } as const,
    transition: { duration: 0.35, delay: index * 0.06, ease: "easeOut" as const },
  };

  const cardClasses = cn(
    "bento-card flex flex-col h-full",
    "bg-[#111B1A] border border-[rgba(255,255,255,0.06)] rounded-[14px]",
    "p-4 transition-all duration-200",
    href && "cursor-pointer hover:-translate-y-[2px] hover:border-[rgba(255,255,255,0.12)] hover:shadow-lg",
    !href && spanClasses[span],
    className
  );

  const cardStyle = glowColor ? { boxShadow: `0 0 30px ${glowColor}10` } : undefined;

  if (href) {
    return (
      <Link to={href} className={cn("block", spanClasses[span], className)}>
        <motion.div {...motionProps} className={cardClasses} style={cardStyle}>
          {cardInner}
        </motion.div>
      </Link>
    );
  }

  return (
    <motion.div {...motionProps} className={cardClasses} style={cardStyle}>
      {cardInner}
    </motion.div>
  );
}
